import { LoadingPage } from '@chiroup/components/LoadingPage';
import {
  SECURE_CASE_ALLOWED_FILE_EXTENSIONS,
  SecureCaseWorkspacePurpose,
} from '@chiroup/core/types/SecureCaseWorkspace.type';
import {
  ArrowLeft,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileLock2,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import secureCaseService from '../../services/secureCase.service';
import Header from '../layout/Header';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-white';
const buttonClass =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClass =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-dark-gray-200 dark:hover:bg-dark-gray-700';
const panelClass =
  'rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-gray-700 dark:bg-dark-gray-900';
const DAY_MS = 24 * 60 * 60 * 1000;

const errorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return 'The request could not be completed.';
};

const formatDate = (value: number | null): string =>
  value ? new Date(value).toLocaleString() : 'Not set';

const retentionLabel = (expiresAt: number, legalHold: boolean): string => {
  if (legalHold) return 'Legal hold - automatic deletion paused';
  const days = Math.max(0, Math.ceil((expiresAt - Date.now()) / DAY_MS));
  return `${days} day${days === 1 ? '' : 's'} until automatic deletion`;
};

const sha256Hex = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
};

const secureFileContentType = (file: File): string => {
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
  return (extension ? SECURE_CASE_ALLOWED_FILE_EXTENSIONS[extension] : '') || file.type || '';
};

const SecureCaseList: React.FC = () => {
  const navigate = useNavigate();
  const [clinicInput, setClinicInput] = useState('');
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [salesforceCase, setSalesforceCase] = useState('');
  const [mondayItemUrl, setMondayItemUrl] = useState('');
  const [purpose, setPurpose] = useState<SecureCaseWorkspacePurpose>('support_case');
  const [error, setError] = useState('');
  const list = useQuery(
    ['secure-cases', clinicId],
    () => secureCaseService.list(clinicId as number),
    { enabled: clinicId != null, refetchOnWindowFocus: false }
  );
  const create = useMutation(() =>
    secureCaseService.create(clinicId as number, {
      clinicId: clinicId as number,
      salesforceCase,
      purpose,
      mondayItemUrl: mondayItemUrl || null,
    })
  );

  const loadClinic = (event: React.FormEvent) => {
    event.preventDefault();
    const next = Number(clinicInput);
    if (!Number.isSafeInteger(next) || next <= 0) {
      setError('Enter a valid clinic ID.');
      return;
    }
    setError('');
    setClinicId(next);
  };

  const createWorkspace = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!clinicId) return;
    setError('');
    try {
      const result = await create.mutateAsync();
      navigate(`/secure-cases/${clinicId}/${result.workspace.id}`);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  return (
    <>
      <Header title="Secure Case Workspaces" />
      <main className="space-y-6 p-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Keep PHI here, never in monday.</p>
              <p className="mt-1">
                monday should contain only the secure workspace link and operational, non-patient
                context. Files are encrypted, scanned, access-controlled, audited, and automatically
                deleted.
              </p>
            </div>
          </div>
        </div>

        <form
          className={`${panelClass} flex flex-col gap-3 sm:flex-row sm:items-end`}
          onSubmit={loadClinic}
        >
          <label className="flex-1 text-sm font-medium text-gray-700 dark:text-dark-gray-200">
            Clinic ID
            <input
              className={`${inputClass} mt-1`}
              inputMode="numeric"
              value={clinicInput}
              onChange={event => setClinicInput(event.target.value)}
              placeholder="1234"
            />
          </label>
          <button className={secondaryButtonClass} type="submit">
            Load workspaces
          </button>
        </form>

        {clinicId && (
          <form className={panelClass} onSubmit={createWorkspace}>
            <div className="mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create secure workspace
              </h2>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <label className="text-sm font-medium text-gray-700 dark:text-dark-gray-200">
                Salesforce case ID or number (optional)
                <input
                  className={`${inputClass} mt-1`}
                  value={salesforceCase}
                  onChange={event => setSalesforceCase(event.target.value)}
                />
              </label>
              <label className="text-sm font-medium text-gray-700 dark:text-dark-gray-200">
                Purpose
                <select
                  className={`${inputClass} mt-1`}
                  value={purpose}
                  onChange={event => setPurpose(event.target.value as SecureCaseWorkspacePurpose)}
                >
                  <option value="support_case">Support case</option>
                  <option value="billing_review">Billing review</option>
                  <option value="implementation">Implementation</option>
                  <option value="security_incident">Security incident</option>
                  <option value="data_import">Data import</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700 dark:text-dark-gray-200">
                monday item URL (optional)
                <input
                  className={`${inputClass} mt-1`}
                  value={mondayItemUrl}
                  onChange={event => setMondayItemUrl(event.target.value)}
                  placeholder="https://chiroup.monday.com/.../pulses/..."
                />
              </label>
            </div>
            <button className={`${buttonClass} mt-4`} type="submit" disabled={create.isLoading}>
              {create.isLoading
                ? salesforceCase.trim()
                  ? 'Verifying case…'
                  : 'Creating workspace…'
                : 'Create secure workspace'}
            </button>
          </form>
        )}

        {error && (
          <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}
        {list.isLoading && <LoadingPage />}
        {list.data && (
          <section className={panelClass}>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Clinic {clinicId} workspaces
            </h2>
            {list.data.items.length === 0 ? (
              <p className="text-sm text-gray-500">No secure workspaces for this clinic.</p>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-dark-gray-700">
                {list.data.items.map(workspace => (
                  <Link
                    key={workspace.id}
                    to={`/secure-cases/${workspace.clinicId}/${workspace.id}`}
                    className="flex flex-col gap-2 py-4 hover:bg-gray-50 dark:hover:bg-dark-gray-800 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {workspace.salesforceCaseNumber
                          ? `Salesforce case ${workspace.salesforceCaseNumber}`
                          : 'Secure workspace'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {workspace.purpose.replaceAll('_', ' ')} · {workspace.documentCount} files ·{' '}
                        {workspace.noteCount} notes
                      </p>
                    </div>
                    <div className="text-sm sm:text-right">
                      <p className="capitalize text-gray-700 dark:text-dark-gray-200">
                        {workspace.status}
                      </p>
                      <p className="text-gray-500">
                        {retentionLabel(workspace.retentionExpiresAt, workspace.legalHold)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </>
  );
};

const SecureCaseDetail: React.FC = () => {
  const params = useParams<{ clinicId: string; workspaceId: string }>();
  const clinicId = Number(params.clinicId);
  const workspaceId = String(params.workspaceId || '');
  const queryClient = useQueryClient();
  const { hasPermission } = useAdminPermissions();
  const [noteBody, setNoteBody] = useState('');
  const [mondayUrl, setMondayUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const shouldSyncSalesforce = useRef(true);
  const detail = useQuery(
    ['secure-case', clinicId, workspaceId],
    async () => {
      const result = await secureCaseService.get(
        clinicId,
        workspaceId,
        shouldSyncSalesforce.current
      );
      shouldSyncSalesforce.current = false;
      return result;
    },
    {
      enabled: Number.isSafeInteger(clinicId) && Boolean(workspaceId),
      refetchInterval: data =>
        data?.documents.some(document => ['uploading', 'scanning'].includes(document.status))
          ? 5000
          : false,
      refetchOnWindowFocus: false,
    }
  );
  const refresh = () => queryClient.invalidateQueries(['secure-case', clinicId, workspaceId]);

  useEffect(() => {
    document.title = 'Secure case workspace';
    return () => {
      document.title = 'ChiroUp Admin';
      queryClient.removeQueries(['secure-case', clinicId, workspaceId]);
    };
  }, [clinicId, queryClient, workspaceId]);
  useEffect(
    () => setMondayUrl(detail.data?.workspace.mondayItemUrl ?? ''),
    [detail.data?.workspace.mondayItemUrl]
  );

  const run = async (request: () => Promise<unknown>) => {
    setError('');
    try {
      await request();
      await refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      await run(async () => {
        const contentType = secureFileContentType(file);
        if (!contentType) {
          throw new Error('This file type is not supported.');
        }
        const checksumSha256 = await sha256Hex(file);
        const initiated = await secureCaseService.initiateUpload(clinicId, workspaceId, {
          fileName: file.name,
          contentType,
          sizeBytes: file.size,
          checksumSha256,
        });
        const form = new FormData();
        Object.entries(initiated.upload.fields).forEach(([key, value]) => form.append(key, value));
        form.append('file', file);
        const uploaded = await fetch(initiated.upload.url, {
          method: 'POST',
          body: form,
          referrerPolicy: 'no-referrer',
        });
        if (!uploaded.ok) throw new Error('The encrypted upload did not complete.');
        await secureCaseService.completeUpload(clinicId, workspaceId, initiated.document.id);
      });
    } finally {
      setUploading(false);
    }
  };

  const openDocument = async (documentId: string, disposition: 'inline' | 'attachment') => {
    await run(async () => {
      const result = await secureCaseService.documentUrl(
        clinicId,
        workspaceId,
        documentId,
        disposition
      );
      window.open(result.url, '_blank', 'noopener,noreferrer');
    });
  };

  if (detail.isLoading) return <LoadingPage />;
  if (!detail.data) {
    return <div className="p-6 text-sm text-red-700">Unable to load secure workspace.</div>;
  }
  const { workspace, documents, notes, auditEvents } = detail.data;
  const hasAccess = hasPermission('secure_cases.view_phi');
  const canContribute = hasAccess && hasPermission('secure_cases.upload') && !workspace.legalHold;
  const canManage = hasPermission('secure_cases.manage');
  const canDownload = hasPermission('secure_cases.download');
  const safeLink = `${window.location.origin}/secure-cases/${clinicId}/${workspaceId}`;

  return (
    <>
      <Header title="Secure Case Workspace" />
      <main className="space-y-6 p-6">
        <Link
          to="/secure-cases"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          All secure workspaces
        </Link>
        <section className={panelClass}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FileLock2 className="h-6 w-6 text-primary-600" />
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  {workspace.salesforceCaseNumber
                    ? `Salesforce case ${workspace.salesforceCaseNumber}`
                    : 'Secure workspace'}
                </h1>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Clinic {workspace.clinicId} · {workspace.purpose.replaceAll('_', ' ')}
                {workspace.salesforceStatus &&
                  ` · Salesforce status: ${workspace.salesforceStatus}`}
              </p>
              <p
                className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${workspace.legalHold ? 'bg-amber-100 text-amber-900' : 'bg-blue-100 text-blue-800'}`}
              >
                {retentionLabel(workspace.retentionExpiresAt, workspace.legalHold)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className={secondaryButtonClass}
                onClick={() => navigator.clipboard.writeText(safeLink)}
              >
                <Copy className="h-4 w-4" />
                Copy monday-safe link
              </button>
              {workspace.salesforceUrl && (
                <a
                  className={secondaryButtonClass}
                  href={workspace.salesforceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                  Salesforce
                </a>
              )}
              {workspace.mondayItemUrl && (
                <a
                  className={secondaryButtonClass}
                  href={workspace.mondayItemUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                  monday
                </a>
              )}
            </div>
          </div>
          {detail.data.salesforceSyncWarning && (
            <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              Salesforce status could not be refreshed. Stored workspace data remains available.
            </p>
          )}
        </section>

        {hasAccess && (
          <>
            <div className="grid gap-6 xl:grid-cols-2">
              <section className={panelClass}>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Encrypted files
                  </h2>
                  {canContribute && workspace.status === 'open' && (
                    <label
                      className={`${buttonClass} ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <UploadCloud className="h-4 w-4" />
                      )}
                      {uploading ? 'Uploading…' : 'Upload file'}
                      <input
                        type="file"
                        className="hidden"
                        disabled={uploading}
                        onChange={event => {
                          const file = event.target.files?.[0];
                          if (file) uploadFile(file);
                          event.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
                <p className="mb-3 text-xs text-gray-500">
                  100 MB maximum. Files cannot be opened until malware scanning passes.
                </p>
                <div className="space-y-3">
                  {documents.length === 0 ? (
                    <p className="text-sm text-gray-500">No files uploaded.</p>
                  ) : (
                    documents.map(document => (
                      <div
                        key={document.id}
                        className="rounded-lg border border-gray-200 p-3 dark:border-dark-gray-700"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                              {document.fileName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(document.sizeBytes / 1024 / 1024).toFixed(2)} MB ·{' '}
                              <span className="capitalize">
                                {document.status.replace('_', ' ')}
                              </span>
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {document.status === 'available' && canDownload && (
                              <>
                                <button
                                  title="Preview"
                                  className={secondaryButtonClass}
                                  onClick={() => openDocument(document.id, 'inline')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </button>
                                <button
                                  title="Download"
                                  className={secondaryButtonClass}
                                  onClick={() => openDocument(document.id, 'attachment')}
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {canContribute && (
                              <button
                                title="Delete"
                                className={secondaryButtonClass}
                                onClick={() =>
                                  window.confirm('Permanently delete this secure file?') &&
                                  run(() =>
                                    secureCaseService.deleteDocument(
                                      clinicId,
                                      workspaceId,
                                      document.id
                                    )
                                  )
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section className={panelClass}>
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Secure notes
                </h2>
                {canContribute && (
                  <div className="mb-4">
                    <textarea
                      className={inputClass}
                      rows={4}
                      value={noteBody}
                      onChange={event => setNoteBody(event.target.value)}
                      placeholder="Put case-specific PHI here, not in monday."
                    />
                    <button
                      className={`${buttonClass} mt-2`}
                      disabled={noteBody.trim().length < 3}
                      onClick={() =>
                        run(async () => {
                          await secureCaseService.createNote(clinicId, workspaceId, {
                            body: noteBody,
                          });
                          setNoteBody('');
                        })
                      }
                    >
                      Add secure note
                    </button>
                  </div>
                )}
                <div className="space-y-3">
                  {notes.length === 0 ? (
                    <p className="text-sm text-gray-500">No secure notes.</p>
                  ) : (
                    notes.map(note => (
                      <article
                        key={note.id}
                        className="rounded-lg border border-gray-200 p-3 dark:border-dark-gray-700"
                      >
                        <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-dark-gray-100">
                          {note.body}
                        </p>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {note.createdByAdminEmail} · {formatDate(note.createdAt)}
                          </span>
                          {canContribute && (
                            <button
                              className="text-red-600 hover:text-red-700"
                              onClick={() =>
                                window.confirm('Delete this secure note?') &&
                                run(() =>
                                  secureCaseService.deleteNote(clinicId, workspaceId, note.id)
                                )
                              }
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </section>
            </div>

            <section className={panelClass}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="flex-1 text-sm font-medium text-gray-700 dark:text-dark-gray-200">
                  Linked monday item
                  <input
                    className={`${inputClass} mt-1`}
                    value={mondayUrl}
                    onChange={event => setMondayUrl(event.target.value)}
                    disabled={!canContribute}
                  />
                </label>
                {canContribute && (
                  <button
                    className={secondaryButtonClass}
                    onClick={() =>
                      run(() =>
                        secureCaseService.update(clinicId, workspaceId, {
                          mondayItemUrl: mondayUrl || null,
                        })
                      )
                    }
                  >
                    Save link
                  </button>
                )}
                {canContribute && workspace.status === 'open' && (
                  <button
                    className={secondaryButtonClass}
                    onClick={() =>
                      window.confirm(
                        'Close this workspace and start the 30-day deletion window?'
                      ) &&
                      run(() =>
                        secureCaseService.update(clinicId, workspaceId, { status: 'closed' })
                      )
                    }
                  >
                    <Clock3 className="h-4 w-4" />
                    Close workspace
                  </button>
                )}
                {hasPermission('secure_cases.legal_hold') && (
                  <button
                    className={secondaryButtonClass}
                    onClick={() => {
                      const reason = window.prompt(
                        workspace.legalHold
                          ? 'Reason for releasing legal hold'
                          : 'Legal hold reason'
                      );
                      if (reason) {
                        run(() =>
                          secureCaseService.legalHold(clinicId, workspaceId, {
                            enabled: !workspace.legalHold,
                            reason,
                          })
                        );
                      }
                    }}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {workspace.legalHold ? 'Release legal hold' : 'Place legal hold'}
                  </button>
                )}
              </div>
            </section>

            {canManage && (
              <section className={panelClass}>
                <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
                  Audit trail
                </h2>
                <div className="max-h-80 divide-y divide-gray-200 overflow-y-auto dark:divide-dark-gray-700">
                  {auditEvents.map(event => (
                    <div
                      key={event.id}
                      className="flex items-start justify-between gap-4 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium text-gray-800 dark:text-dark-gray-100">
                          {event.eventType.replaceAll('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {event.adminEmail || 'System'} · {event.outcome}
                        </p>
                      </div>
                      <time className="shrink-0 text-xs text-gray-500">
                        {formatDate(event.createdAt)}
                      </time>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
        {error && (
          <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}
      </main>
    </>
  );
};

const SecureCases: React.FC = () => {
  const title = useMemo(() => 'Secure Case Workspaces', []);
  return (
    <Routes>
      <Route index element={<SecureCaseList />} />
      <Route path=":clinicId/:workspaceId" element={<SecureCaseDetail />} />
      <Route path="*" element={<div className="p-6">{title} page not found.</div>} />
    </Routes>
  );
};

export default SecureCases;
